import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useDispatch, useSelector } from 'react-redux';
import Select from 'react-select';
import { Button, Stepper, Step, StepLabel, Box, Select as MuiSelect, MenuItem, Checkbox } from '@mui/material';
import { CheckCircle as CheckIcon, ArrowBack as BackIcon, ArrowForward as NextIcon } from '@mui/icons-material';

import './DomainPurchaseWizard.css';
import { getZapmailPlan, getZapmailOrders, searchZapmailDomains, loadMoreZapmailDomains, createZapmailCheckout, cancelPendingZapmailOrders, saveZapmailDraft, getZapmailAdminCostPreview } from '../../store/Zapmail';
import googleIconImg from '../../static/img/google-icon.png';

// Done-For-You email setup wizard — full-screen stepper (same shell as ImportWizard):
// Select Domains -> Add Mailboxes -> Review & Pay (Stripe hosted checkout redirect).
// onClose        — called when the user backs out of the wizard
// salesAccountId — optional; provisioned mailboxes get attached to that Sales Account
const DomainPurchaseWizard = ({ onClose, salesAccountId = null }) => {
    const dispatch = useDispatch();
    const { plan, orders } = useSelector(state => state['Zapmail']) || {};
    const { userDetails } = useSelector(state => state['Settings']) || {};

    const [activeStep, setActiveStep] = useState(0);
    const [form, setForm] = useState(INITIAL_FORM);
    const [ui, setUi] = useState(INITIAL_UI);
    const updateForm = (patch) => setForm(prev => ({ ...prev, ...patch }));
    const updateUi = (patch) => setUi(prev => ({ ...prev, ...patch }));

    useEffect(() => {
        dispatch(getZapmailPlan());
        dispatch(getZapmailOrders());
        setForm(prev => ({ ...prev, draft: { ...prev['draft'], firstName: userDetails?.['firstName'] || '', lastName: userDetails?.['lastName'] || '' } }));
    }, [dispatch, userDetails]);

    // Restore the shared draft after a reload (?draft=<id> lands in the URL on the first save).
    // Drafts live in the orders list as 'draft'-status orders; a missing id falls back silently.
    useEffect(() => {
        if (ui['draftId']) return;
        const draftId = new URLSearchParams(window['location']['search']).get('draft');
        if (!draftId) return;
        updateUi({ resumeDecided: true }); // a restored draft outranks the banners
        const draft = (orders || []).find(order => order['status'] === 'draft' && String(order['_id']) === String(draftId));
        if (!draft) return;
        updateForm(orderItemsToForm(draft));
        updateUi({ draftId: draft['_id'] });
        setActiveStep(Math.min(Math.max(draft['draftStep'] ?? 1, 0), STEPS.length - 1));
    }, [orders]);

    // Persist progress once step 1 is done (and on every later step change) so a reload resumes in place
    const persistDraft = (nextStep) => {
        dispatch(saveZapmailDraft(
            {
                step: nextStep,
                selectedDomains: form['selectedDomains'].map(d => ({ domainName: d['domainName'], price: d['price'], renewPrice: d['renewPrice'] })),
                mailboxRows: form['mailboxRows'].map(row => ({ firstName: row['firstName'], lastName: row['lastName'], username: row['username'], domainName: row['domainName'] })),
                forwardingDomain: form['forwardingDomain'],
                ...(salesAccountId && { salesAccountId })
            },
            (draft) => {
                updateUi({ draftId: draft['_id'] });
                const params = new URLSearchParams(window['location']['search']);
                params.set('draft', draft['_id']);
                window.history.replaceState(null, '', `${window['location']['pathname']}?${params}`);
            }
        ));
    };

    // Offer to resume the workspace's latest unpaid order — visible to the WHOLE team (an admin
    // can pick up and pay for an order a teammate placed but never completed).
    useEffect(() => {
        if (ui['resumeDecided'] || ui['resumeOrder']) return;
        if (form['selectedDomains'].length || form['mailboxRows'].length) return;
        const pending = (orders || []).find(order => order['status'] === 'pending_payment');
        if (pending) updateUi({ resumeOrder: pending });
    }, [orders, userDetails]);

    // Surface the workspace's SHARED draft (possibly a teammate's) when the wizard opens fresh —
    // this is how an admin picks up a junior member's draft to review and pay for it.
    useEffect(() => {
        if (ui['resumeDecided'] || ui['teamDraft'] || ui['draftId']) return;
        if (new URLSearchParams(window['location']['search']).get('draft')) return;
        if (form['selectedDomains'].length || form['mailboxRows'].length) return;
        const draft = (orders || []).find(order => order['status'] === 'draft' && (order['items'] || []).some(item => item['kind'] === 'domain'));
        if (draft) updateUi({ teamDraft: draft });
    }, [orders]);

    // Load the shared team draft into the wizard and continue from its saved step
    const handleContinueTeamDraft = () => {
        const draft = ui['teamDraft'];
        updateForm(orderItemsToForm(draft));
        updateUi({ teamDraft: null, resumeDecided: true, draftId: draft['_id'] });
        const params = new URLSearchParams(window['location']['search']);
        params.set('draft', draft['_id']);
        window.history.replaceState(null, '', `${window['location']['pathname']}?${params}`);
        setActiveStep(Math.min(Math.max(draft['draftStep'] ?? 1, 0), STEPS.length - 1));
    };

    // Rebuild the wizard state from the stored order and jump straight to review
    const handleResume = () => {
        updateForm(orderItemsToForm(ui['resumeOrder']));
        updateUi({ resumeDecided: true, resumeOrder: null });
        setActiveStep(2);
    };

    // Cancels ALL of this user's unpaid orders — the server also supersedes them on the next checkout.
    // The shared team draft is NOT deleted here (it may be a teammate's work) — starting fresh just
    // dismisses the banners; the next save overwrites the shared draft anyway.
    const handleStartNew = () => {
        dispatch(cancelPendingZapmailOrders());
        const params = new URLSearchParams(window['location']['search']);
        params.delete('draft');
        window.history.replaceState(null, '', `${window['location']['pathname']}?${params}`);
        updateUi({ resumeDecided: true, resumeOrder: null, teamDraft: null, draftId: null });
    };

    const mailboxPrice = plan?.['mailboxMonthlyPrice'] || 600;
    const maxPerDomain = plan?.['maxMailboxesPerDomain'] || 5;
    const domainSubtotal = form['selectedDomains'].reduce((sum, d) => sum + d['price'], 0);
    const monthlySubtotal = form['mailboxRows'].length * mailboxPrice;

    const allowedTlds = plan?.['allowedTlds'] || DEFAULT_TLDS;

    // Keep at least one extension selected — an empty TLD list makes the search meaningless
    const handleTldChange = (event) => {
        const selected = event['target']['value'];
        if (selected.length) updateForm({ tlds: selected });
    };

    const tldSummary = () => {
        const ordered = allowedTlds.filter(t => form['tlds'].includes(t));
        return ordered.length === 1 ? `.${ordered[0]}` : `.${ordered[0]} +${ordered.length - 1}`;
    };

    // Registration price per TLD — seeded by the server's cached probe (plan.tldPrices), refined
    // from live search results (registration prices are per-TLD; premium names excluded upstream)
    const tldPrices = { ...(plan?.['tldPrices'] || {}) };
    for (const result of ui['results']) {
        const tld = result['domainName'].split('.').pop();
        if (tldPrices[tld] == null) tldPrices[tld] = result['price'];
    }

    // With a single-TLD result set the price column is noise; across TLDs it lets buyers compare
    const resultTldCount = new Set(ui['results'].map(r => r['domainName'].split('.').pop())).size;

    const handleSearch = () => {
        const query = form['query'].trim();
        if (query.replace(/\.[a-z]+$/i, '').length < 3) return;
        updateUi({ searching: true });
        dispatch(searchZapmailDomains(
            { domainName: query, tlds: form['tlds'] },
            (body) => updateUi({ searching: false, results: (body['domains'] || []).filter(d => d['available']), checked: [], noMore: false }),
            () => updateUi({ searching: false })
        ));
    };

    // Fetch the next batch of generated variants; exclude carries everything already shown or checked.
    // Reason: for popular base words a whole batch can come back registered — auto-advance through up
    // to 3 batches per click so the user sees new chips (or an honest "no more") instead of nothing.
    const runLoadMore = (exclude, attempt) => {
        dispatch(loadMoreZapmailDomains(
            { domainName: form['query'].trim(), exclude },
            (body) => {
                const fresh = (body['domains'] || []).filter(d => d['available']);
                const checked = body['checked'] || [];
                const exhausted = !!body['exhausted'];
                if (!fresh.length && !exhausted && attempt < 2) {
                    setUi(prev => ({ ...prev, checked: [...prev['checked'], ...checked] }));
                    return runLoadMore([...exclude, ...checked], attempt + 1);
                }
                setUi(prev => {
                    const shown = new Set(prev['results'].map(r => r['domainName']));
                    const added = fresh.filter(d => !shown.has(d['domainName']));
                    return { ...prev, loadingMore: false, results: [...prev['results'], ...added], checked: [...prev['checked'], ...checked], noMore: exhausted || !added.length };
                });
            },
            () => updateUi({ loadingMore: false })
        ));
    };

    const handleLoadMore = () => {
        updateUi({ loadingMore: true });
        runLoadMore([...ui['checked'], ...ui['results'].map(r => r['domainName'])], 0);
    };

    const isSelected = (domainName) => form['selectedDomains'].some(d => d['domainName'] === domainName);

    const toggleDomain = (result) => {
        if (isSelected(result['domainName'])) {
            updateForm({
                selectedDomains: form['selectedDomains'].filter(d => d['domainName'] !== result['domainName']),
                mailboxRows: form['mailboxRows'].filter(row => row['domainName'] !== result['domainName'])
            });
        } else {
            updateForm({ selectedDomains: [...form['selectedDomains'], result] });
        }
    };

    const domainMailboxCount = (domainName) => form['mailboxRows'].filter(row => row['domainName'] === domainName).length;

    const handleAddMailbox = () => {
        const { firstName, lastName, username } = form['draft'];
        const domainName = form['draft']['domainName'] || form['selectedDomains'][0]?.['domainName'];
        if (!firstName.trim() || !lastName.trim() || !username.trim() || !domainName) return;
        const cleanUsername = username.trim().toLowerCase();
        if (domainMailboxCount(domainName) >= maxPerDomain) return updateUi({ draftError: `Up to ${maxPerDomain} mailboxes per domain` });
        if (!USERNAME_REGEX.test(cleanUsername)) return updateUi({ draftError: 'Username can use letters, numbers, dots, dashes' });
        if (form['mailboxRows'].some(row => row['username'] === cleanUsername && row['domainName'] === domainName)) return updateUi({ draftError: 'That mailbox is already added' });
        updateUi({ draftError: '' });
        updateForm({
            mailboxRows: [...form['mailboxRows'], { firstName: firstName.trim(), lastName: lastName.trim(), username: cleanUsername, domainName }],
            draft: { ...form['draft'], username: '' }
        });
    };

    const handleRemoveMailbox = (index) => updateForm({ mailboxRows: form['mailboxRows'].filter((_, i) => i !== index) });

    // Platform admins may bypass Stripe in ANY workspace (we cover the Zapmail cost as an admin comp) — server re-checks the role
    const canSkipPayment = userDetails?.['role'] === 'admin';

    // Admin-only wallet/cost preview for the skip-payment button (endpoint rejects non-admins)
    useEffect(() => {
        if (activeStep !== STEPS.length - 1 || !canSkipPayment || !form['selectedDomains'].length) return;
        dispatch(getZapmailAdminCostPreview(
            { domainPrices: form['selectedDomains'].map(d => d['price']), mailboxCount: form['mailboxRows'].length },
            (preview) => updateUi({ adminCostPreview: preview })
        ));
    }, [activeStep]);

    const handlePlaceOrder = (skipPayment = false) => {
        const mailboxesByDomain = {};
        for (const row of form['mailboxRows']) {
            if (!mailboxesByDomain[row['domainName']]) mailboxesByDomain[row['domainName']] = [];
            mailboxesByDomain[row['domainName']].push({ firstName: row['firstName'], lastName: row['lastName'], mailboxUsername: row['username'] });
        }
        updateUi({ placing: true });
        dispatch(createZapmailCheckout(
            { domains: form['selectedDomains'].map(d => ({ domainName: d['domainName'] })), mailboxesByDomain, forwardingDomain: form['forwardingDomain'].trim(), ...(salesAccountId && { salesAccountId }), ...(ui['draftId'] && { draftId: ui['draftId'] }), ...(skipPayment === true && { skipPayment: true }) },
            () => updateUi({ placing: false })
        ));
    };

    const handleNext = () => {
        if (activeStep === STEPS.length - 1) return handlePlaceOrder();
        persistDraft(activeStep + 1);
        setActiveStep(activeStep + 1);
    };

    const handleBack = () => {
        const target = Math.max(0, activeStep - 1);
        if (ui['draftId']) persistDraft(target);
        setActiveStep(target);
    };

    const nextDisabled = ui['placing']
        || (activeStep === 0 && form['selectedDomains'].length === 0)
        || (activeStep === 2 && form['selectedDomains'].length === 0);

    // Reason: the joined email control renders its own '@' separator, so option labels are bare domain names
    const domainOptions = form['selectedDomains'].map(d => ({ value: d['domainName'], label: d['domainName'] }));

    const renderStepContent = () => {
        switch (activeStep) {
            case 0:
                return (
                    <div className='zapmail-wizard-step'>
                        <div className='title-import-modal'>
                            <h2>Find your sending domains</h2>
                            <p>Search for a domain — we register it, create Google mailboxes on it, and connect them for you.</p>
                        </div>
                        <div className='zapmail-wizard-search-row'>
                            <div className='zapmail-wizard-search-joined'>
                                <input type='text' className='form-control zapmail-wizard-search-input' placeholder='yourcompany.com' value={form['query']} disabled={ui['searching']}
                                    onChange={e => updateForm({ query: e['target']['value'] })} onKeyDown={e => e['key'] === 'Enter' && handleSearch()} />
                                <MuiSelect multiple displayEmpty className='zapmail-wizard-tld-select' value={form['tlds']} onChange={handleTldChange} renderValue={tldSummary}>
                                    {allowedTlds.map(tld => (
                                        <MenuItem key={tld} value={tld} className='zapmail-wizard-tld-item'>
                                            <Checkbox size='small' checked={form['tlds'].includes(tld)} />
                                            .{tld}
                                            {tld === 'com' && <span className='zapmail-wizard-tld-badge'>Recommended</span>}
                                            {tldPrices[tld] != null && <span className='zapmail-wizard-tld-price'>{formatPrice(tldPrices[tld])}</span>}
                                        </MenuItem>
                                    ))}
                                </MuiSelect>
                            </div>
                            <button className='btn-style-new' onClick={handleSearch} disabled={ui['searching'] || form['query'].trim().length < 3}>
                                {ui['searching'] ? <><i className='fa fa-spinner fa-spin icon'></i> Searching...</> : 'Search'}
                            </button>
                        </div>

                        {ui['results'].length > 0 && (
                            <div className='zapmail-wizard-suggested'>
                                <span className='zapmail-wizard-suggested-label'>Suggested</span>
                                <div className='zapmail-wizard-chips'>
                                    {ui['results'].map(result => (
                                        <button key={result['domainName']} className={`zapmail-wizard-chip ${isSelected(result['domainName']) ? 'selected' : ''}`} onClick={() => toggleDomain(result)}>
                                            {result['domainName']}
                                            {(resultTldCount > 1 || !result['domainName'].endsWith('.com')) && <span className='zapmail-wizard-chip-price'>{formatPrice(result['price'])}</span>}
                                            <i className={`fa ${isSelected(result['domainName']) ? 'fa-times' : 'fa-check'}`}></i>
                                        </button>
                                    ))}
                                    {!ui['noMore'] && (
                                        <button className='zapmail-wizard-load-more' onClick={handleLoadMore} disabled={ui['loadingMore']}>
                                            {ui['loadingMore'] ? <><i className='fa fa-spinner fa-spin icon mr-1'></i> Loading more...</> : 'Load more domains'}
                                        </button>
                                    )}
                                </div>
                            </div>
                        )}

                        {form['selectedDomains'].length > 0 && (
                            <div className='zapmail-wizard-selected'>
                                <span className='zapmail-wizard-selected-label'>Selected Domains</span>
                                {form['selectedDomains'].map(domain => (
                                    <div key={domain['domainName']} className='zapmail-wizard-selected-row'>
                                        <span className='zapmail-wizard-selected-name'>{domain['domainName']}</span>
                                        <span className='zapmail-wizard-selected-price'>{formatPrice(domain['price'])}</span>
                                        <button className='zapmail-wizard-remove-btn' onClick={() => toggleDomain(domain)}><i className='fa fa-times'></i></button>
                                    </div>
                                ))}
                                <div className='zapmail-wizard-subtotal'>Subtotal (Annual Domain Price): <strong>{formatPrice(domainSubtotal)}</strong></div>
                            </div>
                        )}
                    </div>
                );

            case 1:
                return (
                    <div className='zapmail-wizard-step'>
                        <div className='title-import-modal'>
                            <h2>Order new mailboxes</h2>
                            <p>Add up to {maxPerDomain} mailboxes per domain — each is a real Google mailbox at {formatPrice(mailboxPrice)}/month.</p>
                        </div>
                        <div className='zapmail-wizard-forwarding-box'>
                            <label className='zapmail-wizard-label'>Forwarding Domain</label>
                            <input type='text' className='form-control zapmail-wizard-forwarding-input' placeholder='https://yourcompany.com' value={form['forwardingDomain']} onChange={e => updateForm({ forwardingDomain: e['target']['value'] })} />
                            <span className='zapmail-wizard-forwarding-hint'>Optional — visitors to your new domains are redirected here</span>
                        </div>

                        <div className='zapmail-wizard-persona-box'>
                            <div className='zapmail-wizard-persona-row'>
                                <div className='form-group'>
                                    <label className='zapmail-wizard-label'>First Name</label>
                                    <input type='text' className='form-control' value={form['draft']['firstName']} onChange={e => updateForm({ draft: { ...form['draft'], firstName: e['target']['value'] } })} />
                                </div>
                                <div className='form-group'>
                                    <label className='zapmail-wizard-label'>Last Name</label>
                                    <input type='text' className='form-control' value={form['draft']['lastName']} onChange={e => updateForm({ draft: { ...form['draft'], lastName: e['target']['value'] } })} />
                                </div>
                            </div>
                            <div className='form-group zapmail-wizard-email-group'>
                                <label className='zapmail-wizard-label'>Email</label>
                                <div className='zapmail-wizard-email-joined'>
                                    <input type='text' className='form-control zapmail-wizard-email-input' placeholder='firstname' value={form['draft']['username']}
                                        onChange={e => updateForm({ draft: { ...form['draft'], username: e['target']['value'] } })} onKeyDown={e => e['key'] === 'Enter' && handleAddMailbox()} />
                                    <span className='zapmail-wizard-email-at'>@</span>
                                    <Select classNamePrefix='zapmail-wizard-select' className='zapmail-wizard-domain-select' options={domainOptions}
                                        value={domainOptions.find(o => o['value'] === (form['draft']['domainName'] || form['selectedDomains'][0]?.['domainName'])) || null}
                                        onChange={option => updateForm({ draft: { ...form['draft'], domainName: option['value'] } })} />
                                </div>
                            </div>
                            {ui['draftError'] && <div className='zapmail-wizard-draft-error'>{ui['draftError']}</div>}
                            <button className='btn-style-new zapmail-wizard-add-btn' onClick={handleAddMailbox}>Add</button>
                        </div>

                        {form['mailboxRows'].length > 0 && (
                            <div className='zapmail-wizard-rows'>
                                <div className='zapmail-wizard-row zapmail-wizard-row-head'>
                                    <span>Provider</span><span>Persona</span><span>Email</span><span>Price</span><span></span>
                                </div>
                                {form['mailboxRows'].map((row, index) => (
                                    <div key={`${row['username']}@${row['domainName']}`} className='zapmail-wizard-row'>
                                        <span className='zapmail-wizard-provider-pill'><img className='zapmail-wizard-provider-icon' alt='' src={googleIconImg} /> Google</span>
                                        <span>{row['firstName']} {row['lastName']}</span>
                                        <span className='zapmail-wizard-row-email'>{row['username']}@{row['domainName']}</span>
                                        <span>{formatPrice(mailboxPrice)}/mo</span>
                                        <button className='zapmail-wizard-remove-btn' title='Remove mailbox' onClick={() => handleRemoveMailbox(index)}><i className='fa fa-times'></i></button>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                );

            case 2:
                return (
                    <div className='zapmail-wizard-step'>
                        <div className='title-import-modal'>
                            <h2>Review your order</h2>
                            <p>Payment is processed securely by Stripe. Setup starts right after checkout and usually completes within an hour.</p>
                        </div>
                        <div className='zapmail-wizard-review-box'>
                            <div className='zapmail-wizard-review-section'>
                                <span className='zapmail-wizard-review-heading'>Domains ({form['selectedDomains'].length}) — billed yearly</span>
                                {form['selectedDomains'].map(domain => (
                                    <div key={domain['domainName']} className='zapmail-wizard-review-line'>
                                        <span>{domain['domainName']}</span>
                                        <span>{formatPrice(domain['price'])}/yr</span>
                                    </div>
                                ))}
                            </div>
                            {form['mailboxRows'].length > 0 && (
                                <div className='zapmail-wizard-review-section'>
                                    <span className='zapmail-wizard-review-heading'>Mailboxes ({form['mailboxRows'].length}) — billed monthly</span>
                                    {form['mailboxRows'].map(row => (
                                        <div key={`${row['username']}@${row['domainName']}`} className='zapmail-wizard-review-line'>
                                            <span>{row['username']}@{row['domainName']}</span>
                                            <span>{formatPrice(mailboxPrice)}/mo</span>
                                        </div>
                                    ))}
                                </div>
                            )}
                            <div className='zapmail-wizard-summary'>
                                <div className='zapmail-wizard-summary-lines'>
                                    <div className='zapmail-wizard-summary-line'><span>Subtotal (Monthly Price)</span><strong>{formatPrice(monthlySubtotal)}</strong></div>
                                    <div className='zapmail-wizard-summary-line'><span>Domain Annual Price</span><strong>{formatPrice(domainSubtotal)}</strong></div>
                                    <div className='zapmail-wizard-summary-line total'><span>Total Due Today</span><strong>{formatPrice(monthlySubtotal + domainSubtotal)}</strong></div>
                                </div>
                                <div className='zapmail-wizard-summary-note'>Mailboxes renew monthly, domains renew yearly. New mailboxes appear in Sales Accounts as “Setup Pending” until they activate.</div>
                            </div>
                        </div>
                    </div>
                );

            default:
                return null;
        }
    };

    // Reason: rendered into document.body via portal — inside the app layout an ancestor's
    // stacking context (main panel transform) caps our z-index below the sidebar/logo.
    return createPortal(
        <div className='single-agent-page single-contact-page import-page zapmail-wizard'>
            <div className='page-header-new style-two'>
                <h2>
                    <div className='right-heading-area'>
                        <h2>
                            <button className='icon-btn-back' onClick={onClose}>
                                <svg width='8' height='14' viewBox='0 0 8 14' fill='none' xmlns='http://www.w3.org/2000/svg'>
                                    <path d='M7 13L1 7L7 1' stroke='black' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round' />
                                </svg>
                            </button>
                            Done-For-You Email Setup</h2>
                    </div>
                </h2>
            </div>
            <div className='bottom-content-area contact-details-area import-wizard-popup'>
                <Stepper activeStep={activeStep} sx={{ mb: 4 }} alternativeLabel>
                    {STEPS.map((label) => <Step key={label}> <StepLabel>{label}</StepLabel> </Step>)}
                </Stepper>
                {ui['resumeOrder'] && !ui['resumeDecided'] && (
                    <div className='zapmail-wizard-resume'>
                        <div className='zapmail-wizard-resume-text'>
                            <strong>Continue an unfinished order?</strong>
                            <span>{resumeSummary(ui['resumeOrder'], userDetails?.['_id'])} — placed but never paid.</span>
                        </div>
                        <div className='zapmail-wizard-resume-btns'>
                            <button className='btn-style-border' onClick={handleStartNew}>Start new</button>
                            <button className='btn-style-new' onClick={handleResume}>Resume order</button>
                        </div>
                    </div>
                )}
                {!ui['resumeOrder'] && ui['teamDraft'] && !ui['resumeDecided'] && (
                    <div className='zapmail-wizard-resume'>
                        <div className='zapmail-wizard-resume-text'>
                            <strong>Continue your team's draft?</strong>
                            <span>{draftSummary(ui['teamDraft'], userDetails?.['_id'])} — you can review, edit and pay for it.</span>
                        </div>
                        <div className='zapmail-wizard-resume-btns'>
                            <button className='btn-style-border' onClick={handleStartNew}>Start new</button>
                            <button className='btn-style-new' onClick={handleContinueTeamDraft}>Continue draft</button>
                        </div>
                    </div>
                )}
                {renderStepContent()}
            </div>
            <div className='bottom-btns-area'>
                <Button onClick={handleBack} startIcon={<BackIcon />} className='btn-style-border' disabled={activeStep === 0 || ui['placing']}>
                    Back
                </Button>
                <Button className='btn-cancel-style' onClick={onClose} disabled={ui['placing']}>
                    Cancel
                </Button>
                <Box sx={{ flex: 1 }} />
                {activeStep === STEPS.length - 1 && canSkipPayment && (
                    <div className='zapmail-wizard-admin-skip'>
                        {ui['adminCostPreview'] && (
                            <div className='zapmail-wizard-admin-skip-stats'>
                                <span className='zapmail-wizard-admin-skip-stat'>
                                    <span className='zapmail-wizard-admin-skip-stat-label'>Zapmail Balance</span>
                                    <span className='zapmail-wizard-admin-skip-stat-value'>{ui['adminCostPreview']['walletBalance'] == null ? '—' : `$${Number(ui['adminCostPreview']['walletBalance']).toLocaleString('en-US', { minimumFractionDigits: 2 })}`}</span>
                                </span>
                                <span className={`zapmail-wizard-admin-skip-stat ${walletShort(ui['adminCostPreview']) ? 'short' : ''}`}>
                                    <span className='zapmail-wizard-admin-skip-stat-label'>Zapmail Cost</span>
                                    <span className='zapmail-wizard-admin-skip-stat-value'>~${(ui['adminCostPreview']['totalCost'] / 100).toFixed(2)}{walletShort(ui['adminCostPreview']) && ' — wallet short!'}</span>
                                </span>
                            </div>
                        )}
                        <Button onClick={() => handlePlaceOrder(true)} disabled={nextDisabled} className='btn-style-border'>
                            Place Without Payment
                        </Button>
                    </div>
                )}
                <Button onClick={handleNext} disabled={nextDisabled} className='btn-style-new' endIcon={activeStep === STEPS.length - 1 ? <CheckIcon /> : <NextIcon />}>
                    {activeStep === STEPS.length - 1 ? (ui['placing'] ? 'Redirecting...' : 'Place Order') : 'Next'}
                </Button>
            </div>
        </div>,
        document.body
    );
};

const STEPS = ['Select Domains', 'Add Mailboxes', 'Review & Pay'];

const USERNAME_REGEX = /^(?!.*[._-]{2})[a-z0-9](?:[a-z0-9._-]*[a-z0-9])?$/;

const DEFAULT_TLDS = ['com', 'net', 'org', 'biz', 'live', 'info'];

const INITIAL_FORM = { query: '', tlds: ['com'], selectedDomains: [], mailboxRows: [], forwardingDomain: '', draft: { firstName: '', lastName: '', username: '', domainName: '' } };
const INITIAL_UI = { searching: false, placing: false, loadingMore: false, noMore: false, results: [], checked: [], draftError: '', resumeOrder: null, resumeDecided: false, draftId: null, teamDraft: null, adminCostPreview: null };

// Wallet (whole USD) vs estimated order cost (cents)
const walletShort = (preview) => preview['walletBalance'] != null && preview['walletBalance'] * 100 < preview['totalCost'];

// Prices arrive from the server in cents
const formatPrice = (cents) => `$${(cents / 100).toFixed(2)}`;

// "2 domains and 4 mailboxes" summary for the resume banner
// Rebuild wizard form state from an order/draft doc's items (drafts and pending orders share the shape)
const orderItemsToForm = (order) => ({
    selectedDomains: (order['items'] || []).filter(i => i['kind'] === 'domain').map(i => ({ domainName: i['domainName'], price: i['retailAmount'] || 0, available: true })),
    mailboxRows: (order['items'] || []).filter(i => i['kind'] === 'mailbox_plan').flatMap(i => (i['mailboxes'] || []).map(m => ({ firstName: m['firstName'], lastName: m['lastName'], username: m['mailboxUsername'], domainName: m['domainName'] }))),
    forwardingDomain: (order['items'] || []).find(i => i['kind'] === 'domain' && i['forwardingDomain'])?.['forwardingDomain'] || ''
});

// "Malik" for a populated teammate, "You" for the current user, safe fallback otherwise
const authorName = (user, myUserId) => {
    if (String(user?.['_id'] || user) === String(myUserId)) return 'You';
    return user && typeof user === 'object' ? (`${user['firstName'] || ''} ${user['lastName'] || ''}`.trim() || 'A teammate') : 'A teammate';
};

const orderCounts = (order) => {
    const domainCount = (order['items'] || []).filter(i => i['kind'] === 'domain').length;
    const mailboxCount = (order['items'] || []).filter(i => i['kind'] === 'mailbox_plan').reduce((n, i) => n + (i['mailboxes']?.length || 0), 0);
    const parts = [];
    if (domainCount) parts.push(`${domainCount} domain${domainCount > 1 ? 's' : ''}`);
    if (mailboxCount) parts.push(`${mailboxCount} mailbox${mailboxCount > 1 ? 'es' : ''}`);
    return parts.join(' and ');
};

// Shared-draft banner line: who started it, what's in it, and when it last moved
const draftSummary = (draft, myUserId) => {
    const author = authorName(draft['userId'], myUserId);
    return `${author} started a purchase draft with ${orderCounts(draft)}, last updated ${new Date(draft['updatedAt']).toLocaleString()}`;
};

const resumeSummary = (order, myUserId) => {
    const author = authorName(order['userId'], myUserId);
    return `${author} ${author === 'You' ? 'have' : 'has'} an unfinished order with ${orderCounts(order)}`;
};

export default DomainPurchaseWizard;
